import React, { createContext, useContext, useState, useEffect } from 'react';
import { projectId } from '../utils/supabase/info';
import { getValidAccessToken } from '../utils/auth';
import { toast } from 'sonner';
import { WebsiteData } from '@/types/WebsiteData';

type WebsiteContextType = {
  websites: WebsiteData[];
  selectedWebsite: WebsiteData | null;
  setSelectedWebsite: (website: WebsiteData | null) => void;
  isLoading: boolean;
  refreshWebsites: () => Promise<void>;
};

const WebsiteContext = createContext<WebsiteContextType | undefined>(undefined);

export function WebsiteProvider({ children }: { children: React.ReactNode }) {
  const [websites, setWebsites] = useState<WebsiteData[]>([]);
  const [selectedWebsite, setSelectedWebsite] = useState<WebsiteData | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Cache configuration
  const CACHE_KEY_WEBSITES = 'cachedWebsites';
  const CACHE_KEY_TIMESTAMP = 'cacheTimestamp';
  const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

  const loadFromCache = (): WebsiteData[] | null => {
    const cached = localStorage.getItem(CACHE_KEY_WEBSITES);
    const timestampStr = localStorage.getItem(CACHE_KEY_TIMESTAMP);
    if (cached && timestampStr) {
      const timestamp = parseInt(timestampStr, 10);
      if (Date.now() - timestamp < CACHE_DURATION) {
        try {
          return JSON.parse(cached) as WebsiteData[];
        } catch (error) {
          localStorage.removeItem(CACHE_KEY_WEBSITES);
          localStorage.removeItem(CACHE_KEY_TIMESTAMP);
        }
      }
    }
    return null;
  };

  const saveToCache = (websitesData: WebsiteData[]) => {
    try {
      localStorage.setItem(CACHE_KEY_WEBSITES, JSON.stringify(websitesData));
      localStorage.setItem(CACHE_KEY_TIMESTAMP, Date.now().toString());
    } catch (error) {
    }
  };

  const clearCache = () => {
    localStorage.removeItem(CACHE_KEY_WEBSITES);
    localStorage.removeItem(CACHE_KEY_TIMESTAMP);
  };

  const fetchWebsites = async (preserveSelection = false, forceRefresh = false) => {
    const startTime = Date.now();
    try {

      // Check cache first unless force refresh
      if (!forceRefresh) {
        const cachedWebsites = loadFromCache();
        if (cachedWebsites && cachedWebsites.length > 0) {
          setWebsites(cachedWebsites);
          
          if (preserveSelection && selectedWebsite) {
            const stillExists = cachedWebsites.find((w: WebsiteData) => w.id === selectedWebsite.id);
            if (stillExists) {
              setSelectedWebsite(stillExists);
            } else if (cachedWebsites.length > 0) {
              setSelectedWebsite(cachedWebsites[0]);
            } else {
              setSelectedWebsite(null);
            }
          } else {
            const savedId = localStorage.getItem('selectedWebsiteId');
            let toSelect: WebsiteData | null = null;

            if (savedId) {
              const savedWebsite = cachedWebsites.find((w: WebsiteData) => w.id.toString() === savedId);
              if (savedWebsite) {
                toSelect = savedWebsite;
              } else {
                localStorage.removeItem('selectedWebsiteId');
              }
            }

            if (!toSelect && cachedWebsites.length > 0) {
              toSelect = cachedWebsites[0];
            }

            setSelectedWebsite(toSelect);
          }
          setIsLoading(false);
          return; // Early return if using cache
        }
      }

      // Get a valid access token (will refresh if needed)
      const accessToken = await getValidAccessToken();
      const tokenTime = Date.now() - startTime;
      
      if (!accessToken) {
        setWebsites([]);
        setSelectedWebsite(null);
        setIsLoading(false);
        return;
      }

      // Fetch websites with 15-second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
      }, 15000);
      
      try {
        const fetchStartTime = Date.now();
        
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-3b83895f/websites`,
          {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
            signal: controller.signal,
          }
        );
        
        clearTimeout(timeoutId);
        const fetchTime = Date.now() - fetchStartTime;

        if (response.ok) {
          const data = await response.json();
          
          if (data.websites && data.websites.length > 0) {
            setWebsites(data.websites);
            saveToCache(data.websites); // Save to cache after successful fetch
            
            if (preserveSelection && selectedWebsite) {
              // Try to keep the current selection if it still exists
              const stillExists = data.websites.find((w: WebsiteData) => w.id === selectedWebsite.id);
              if (stillExists) {
                setSelectedWebsite(stillExists);
              } else {
                setSelectedWebsite(data.websites[0]);
              }
            } else {
              // Initial load or no current selection - check localStorage for persistence
              const savedId = localStorage.getItem('selectedWebsiteId');
              let toSelect: WebsiteData | null = null;

              if (savedId) {
                const savedWebsite = data.websites.find((w: WebsiteData) => w.id.toString() === savedId);
                if (savedWebsite) {
                  toSelect = savedWebsite;
                } else {
                  // Saved website no longer exists, clear from localStorage
                  localStorage.removeItem('selectedWebsiteId');
                }
              }

              if (!toSelect && data.websites.length > 0) {
                toSelect = data.websites[0];
              }

              setSelectedWebsite(toSelect);
            }
          } else {
            setWebsites([]);
            setSelectedWebsite(null);
            localStorage.removeItem('selectedWebsiteId');
            clearCache();
          }
        } else if (response.status === 401) {
          // Don't clear token here - let the auth utility handle it
          // Don't show toast - user might be logging out or token refresh will handle it
          setWebsites([]);
          setSelectedWebsite(null);
          localStorage.removeItem('selectedWebsiteId');
          clearCache();
        } else {
          try {
            const errorData = await response.json();
            
            // Show helpful error message if table doesn't exist
            if (errorData.error?.includes('does not exist')) {
              toast.error('Database Setup Required', {
                description: 'Please run FIX-WEBSITE-TABLE-NOW.sql in your Supabase SQL Editor',
                duration: 10000,
              });
            } else {
              toast.error(`Error loading websites: ${errorData.error || errorData.message || 'Unknown error'}`);
            }
          } catch (parseError) {
            const errorText = await response.text().catch(() => 'Unable to read error');
            toast.error('Error loading websites. Please check the console for details.');
          }
        }
      } catch (fetchError: any) {
        clearTimeout(timeoutId);
        
        if (fetchError.name === 'AbortError') {
          toast.error('Request timed out loading websites', {
            description: 'The website table may not exist. Please run FIX-WEBSITE-TABLE-NOW.sql in your Supabase SQL Editor.',
            duration: 10000,
          });
        } else {
          toast.error(`Network error: ${fetchError.message || 'Unable to connect to server'}`);
        }
      }
    } catch (error: any) {
    } finally {
      const totalTime = Date.now() - startTime;
      setIsLoading(false);
    }
  };

  const refreshWebsites = async () => {
    setIsLoading(true);
    await fetchWebsites(true, true); // preserveSelection=true, forceRefresh=true
  };

  // Persist selectedWebsite to localStorage on change
  useEffect(() => {
    if (selectedWebsite) {
      localStorage.setItem('selectedWebsiteId', selectedWebsite.id.toString());
    } else {
      localStorage.removeItem('selectedWebsiteId');
    }
  }, [selectedWebsite]);

  useEffect(() => {
    let mounted = true;

    // Wrapper to only fetch if component is still mounted
    const safeFetchWebsites = async (preserveSelection: boolean) => {
      if (!mounted) {
        return;
      }
      await fetchWebsites(preserveSelection);
    };

    // Initial fetch
    safeFetchWebsites(false);

    // Listen for website added event
    const handleWebsiteAdded = async () => {
      await refreshWebsites(); // Use refreshWebsites to force fetch and preserve selection
    };

    // Listen for logout event to clear data
    const handleLogout = () => {
      if (mounted) {
        setWebsites([]);
        setSelectedWebsite(null);
        localStorage.removeItem('selectedWebsiteId');
        clearCache();
        setIsLoading(false);
      }
    };

    window.addEventListener('websiteAdded', handleWebsiteAdded);
    window.addEventListener('userLoggedOut', handleLogout);

    return () => {
      mounted = false;
      window.removeEventListener('websiteAdded', handleWebsiteAdded);
      window.removeEventListener('userLoggedOut', handleLogout);
    };
  }, []);

  return (
    <WebsiteContext.Provider
      value={{
        websites,
        selectedWebsite,
        setSelectedWebsite,
        isLoading,
        refreshWebsites,
      }}
    >
      {children}
    </WebsiteContext.Provider>
  );
}

export function useWebsite() {
  const context = useContext(WebsiteContext);
  if (context === undefined) {
    throw new Error('useWebsite must be used within a WebsiteProvider');
  }
  return context;
}